// Authentication and company status utilities
import { LocalStorageUser, LocalStorageMap } from '../types/localStorage.d';
import { CompanyDetails, CompanyVerificationStatus } from '../types/api.d';

// Helper to get typed item from localStorage
export function getLocalStorageItem<K extends keyof LocalStorageMap>(key: K): LocalStorageMap[K] | null {
  if (typeof window === 'undefined') return null;
  const item = localStorage.getItem(key);
  if (item === null) return null;

  // Special handling for 'user' which is stored as a JSON string
  if (key === 'user') {
    try {
      return JSON.parse(item) as LocalStorageMap[K];
    } catch (e) {
      console.error(`Error parsing localStorage item "${key}":`, e);
      return null;
    }
  }
  return item as LocalStorageMap[K];
}

// Helper to set typed item to localStorage
function setLocalStorageItem<K extends keyof LocalStorageMap>(key: K, value: LocalStorageMap[K]): void {
  if (typeof window === 'undefined') return;
  // Special handling for 'user' which should be stored as a JSON string
  if (key === 'user' && typeof value !== 'string') {
    localStorage.setItem(key, JSON.stringify(value));
  } else {
    localStorage.setItem(key, value as string);
  }
}

// Helper to remove item from localStorage
function removeLocalStorageItem<K extends keyof LocalStorageMap>(key: K): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(key);
}


export interface SimplifiedCompanyStatus {
  status: 'Verified' | 'Pending' | 'Rejected' | 'NotRegistered';
  message: string;
  canAccessDashboard: boolean;
  shouldShowApprovalScreen: boolean;
  shouldRedirectToRegistration: boolean;
}

export const getCompanyStatus = (companyDetails: CompanyDetails | null): SimplifiedCompanyStatus => {
  if (!companyDetails) {
    return {
      status: 'NotRegistered',
      message: 'Company registration not submitted',
      canAccessDashboard: false,
      shouldShowApprovalScreen: false,
      shouldRedirectToRegistration: true
    };
  }

  switch (companyDetails.verificationStatus) {
    case 'Verified':
      return {
        status: 'Verified',
        message: 'Company is approved and active',
        canAccessDashboard: true,
        shouldShowApprovalScreen: false,
        shouldRedirectToRegistration: false
      };
    case 'Pending':
      return {
        status: 'Pending',
        message: 'Company registration is pending approval',
        canAccessDashboard: true, // Allow access to dashboard, banner will show
        shouldShowApprovalScreen: false,
        shouldRedirectToRegistration: false
      };
    case 'Rejected':
      return {
        status: 'Rejected',
        message: 'Company registration was rejected',
        canAccessDashboard: false,
        shouldShowApprovalScreen: false,
        shouldRedirectToRegistration: true // Redirect to registration to re-apply
      };
    default:
      return {
        status: 'NotRegistered',
        message: 'Unknown company status',
        canAccessDashboard: false,
        shouldShowApprovalScreen: false,
        shouldRedirectToRegistration: true
      };
  }
};

export const clearAllAuthData = (): void => {
  // Clear authentication data
  removeLocalStorageItem('token');
  removeLocalStorageItem('user');
  
  // Clear company-specific data
  removeLocalStorageItem('companyRegistrationStatus');
  removeLocalStorageItem('companyRegistrationSubmitted');
  
  // Clear any other app-specific data
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('app_')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
};

export const setCompanyRegistrationSubmitted = (): void => {
  setLocalStorageItem('companyRegistrationSubmitted', 'true');
  removeLocalStorageItem('companyRegistrationStatus'); // Remove any previous approval
};

export const setCompanyApproved = (): void => {
  setLocalStorageItem('companyRegistrationStatus', 'completed');
  removeLocalStorageItem('companyRegistrationSubmitted'); // No longer pending
};

export const resetCompanyStatus = (): void => {
  removeLocalStorageItem('companyRegistrationStatus');
  removeLocalStorageItem('companyRegistrationSubmitted');
};

export const isUserAuthenticated = (): boolean => {
  const token = getLocalStorageItem('token');
  const user = getLocalStorageItem('user');
  return !!(token && user);
};

export const getUserRole = (): LocalStorageUser['role'] | null => {
  const userStr = getLocalStorageItem('user');
  if (!userStr) return null;
  
  try {
    const user: LocalStorageUser = JSON.parse(userStr);
    return user?.role as LocalStorageUser['role'];
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

export const shouldShowApprovalScreen = (userRole: LocalStorageUser['role'] | null, companyDetails: CompanyDetails | null): boolean => {
  if (userRole !== 'Company') return false;
  
  const companyStatus = getCompanyStatus(companyDetails);
  return companyStatus.shouldShowApprovalScreen;
};

export const getRedirectPath = (userRole: LocalStorageUser['role'] | null): string => {
  if (!userRole) return '/login';
  
  
  switch (userRole) {
    case 'Company':
      return "/company/";
      // if (companyStatus.shouldRedirectToRegistration) {
      //   return '/company/register';
      // } else {
      //   return '/company/dashboard';
      // }
    case 'Agent':
      // Check if agent profile is complete
      return '/agent/dashboard'; // Always go to dashboard, modal logic is handled there
    case 'Admin':
      // Check if agent profile is complete
      return '/admin/dashboard'; // Always go to dashboard, modal logic is handled there
    case 'Jobseeker':
    default:
      // Check if user profile is complete using unified completion flag or saved profile data
      try {
        const userStr = getLocalStorageItem('user');
        return '/user/dashboard';
      } catch (e) {
        return '/user/dashboard';
      }
  }
};
